import { Event } from "../../../base/common/events.js";
import type { RendererSettings } from "./rendererSettings.js";
import { createDecorator } from "../../instantiation/common/registry.js";
import type { ProgramInfo, RenderStatsSummary, RenderInfoStats } from "./renderStats.js";
import type { SceneTriangleCounts } from "./sceneStats.js";


export const IWebGLService = createDecorator<IWebGLService>("webglService");

export interface WebGLServiceChangedEvent {
    readonly service: IWebGLService;
}

/**
 * Provides WebGL-related functionalities and manages available WebGL engines. E.g., Three.js, Babylon.js, etc.
 */
export interface IWebGLService {

    readonly onDidChange: Event<WebGLServiceChangedEvent>;

    registerEngine(engine: IWebGLEngine): void;
    hasEngine(name: WebGLEngineName): boolean;
    getEngine<T extends IWebGLEngine>(name: WebGLEngineName): T | undefined;
    getAllEngines(): IWebGLEngine[];

}


export type WebGLEngineName = "threejs" | "babylonjs";

export interface WebGLEngineChangedEvent {
    readonly engine: IWebGLEngine;
}

/**
 * Implement to provide WebGL engine access like Three.js, Babylon.js, etc.  
 */
export interface IWebGLEngine {
    readonly name: WebGLEngineName;
    readonly onDidChange: Event<WebGLEngineChangedEvent>;
    // connect(bridge: IWebGLSceneBridge): void;
    // disconnect(bridge: IWebGLSceneBridge): void;

    // registerRenderer?(renderer: unknown): void;
    getScene?<TScene = unknown>(): TScene | null;
    getRenderer?<TRenderer = unknown>(): TRenderer | null;
    getCamera?<TCamera = unknown>(): TCamera | null;
    
    getScenes?<TScene = unknown>(): TScene[] | null;

    findObjectById?(id: string): unknown | null;

    /**
     * What the main renderer draws per displayed frame, averaged over the window.
     *
     * Averaged, not per-frame, because there is no real frame boundary to read:
     * a postprocessed scene runs several `render()` calls per displayed frame and
     * nothing hooks the host app's rAF. `frames` is what the caller measured
     * (fps), so the numbers come out per displayed frame. Null when nothing
     * rendered in the window — a still editor should not show a stale number.
     */
    getRenderStats?(opts: { windowMs: number, frames: number }): RenderStatsSummary | null;

    /**
     * The latest backend-agnostic render numbers for the main renderer — per-frame
     * and lifetime draw calls / triangles, with the WebGL/WebGPU differences already
     * resolved (see {@link RenderInfoStats}). Null until the renderer has drawn.
     */
    getRenderInfoStats?(): RenderInfoStats | null;

    /**
     * Triangles in the CURRENT scene — `visible` (what the camera would draw:
     * invisible pruned, instances expanded) for the live toolbar number, and
     * `total` (incl. hidden) for the tooltip. This is the scene's geometry, not
     * the GPU's per-frame work (see getRenderStats) — a mesh counts once no matter
     * how many passes draw it. Null when there is no scene. A cheap, ref-free walk.
     */
    getSceneTriangleCounts?(): SceneTriangleCounts | null;

    /**
     * Compiled shader programs across this engine's renderers, summed. A program
     * compiles on its FIRST render and stalls the frame while it does, so this is
     * sampled over time (Assets table) rather than read once: a step up mid-session
     * IS a compile that just happened. Null when no renderer reports it.
     */
    getProgramInfo?(): ProgramInfo | null;

    /**
     * Every renderer this engine has seen draw. A page can have several — a
     * WebGLRenderer AND a WebGPURenderer is a real combination — and
     * {@link getRenderer} answers with the LAST one to render, which is not
     * necessarily the one a caller wants.
     */
    getRenderers?(): unknown[];
    /** live renderers with their type and whether they drew recently — a report
     *  has to say WHICH renderer the per-frame numbers belong to */
    describeRenderers?(recentMs?: number): { type: string; active: boolean; settings?: RendererSettings }[];

    editorCamera?: boolean;

    // "fly a pass" (mode 2): enumerate the render passes and take the editor
    // camera fullscreen into any one of them (e.g. a baked subscene).
    getRenderPassInfos?(): Array<{ id: string; label: string; cells?: string[]; disabled?: boolean; title?: string; hierarchyNodeId?: string; presentation: "camera" | "other" }>;
    setFlyPass?(passId: string | null): void;
    getFlyPassId?(): string | null;
    resetEditorCamera?(): boolean;
    /** Repaint the current editor-camera pass after its canvas presentation changes. */
    requestEditorCameraRender?(): boolean;
    /** Adopt render calls observed by the extension before core attached its scene hooks. */
    adoptStartupRenderPasses?(passes: readonly StartupRenderPass[]): boolean;
}

export interface StartupRenderPass {
    readonly renderer: object;
    readonly scene: object;
    readonly camera: object;
    readonly writesCanvas: boolean;
}
/**
 * Bridge interface to connect WebGL engines with the application/editor.
 */
// export interface IWebGLSceneBridge<
//     TCamera = unknown,
//     TScene = unknown,
//     TRenderer = unknown,
//     TRaycastResult = unknown,
//     TFitCameraResult = void,
// > {
//     getCamera?(): TCamera;
//     getScene?(): TScene;
//     getRenderer?(): TRenderer;
//     /** @deprecated */
//     raycast?(raycaster: unknown): TRaycastResult;
//     /** @deprecated */
//     fitCamera?(objects: unknown[]): TFitCameraResult;
// }
